import React, { useState } from "react";
import { connect } from "react-redux";
import { useFormContext, useWatch } from "react-hook-form";
import { GoogleMap, Marker } from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  geocodeByPlaceId,
} from "react-places-autocomplete";
import { setKey, fromLatLng } from "react-geocode";

import { Location } from "../../../../assets";
import { EMPTY_STRING } from "../../../../constants";
import { getSelectOptions } from "../../../../shared/utility";
import CustomSelect from "../../../../shared/components/select/CustomSelect";

const options = {
  zoomControl: true,
  disableDefaultUI: true, //disable the default controls
};

const containerStyle = {
  width: "100%",
  height: "250px",
};

const FORM_REGISTER_KEY = {
  LATITUDE: "latitude",
  LONGITUDE: "longitude",
  COUNTRY: "countryId",
  CITY: "city",
  STATE: "state",
  ADDRESS_LINE_1: "addressLine1",
};

setKey(process.env.REACT_APP_GOOGLE_API_KEY);

const AddressAndLocation = (props) => {
  const {
    countryReducer: { allCountries },
  } = props;

  const {
    control,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  // Use useWatch to subscribe to changes in "latitude" and "longitude"
  const coordinates = {
    lat: useWatch({ control, name: FORM_REGISTER_KEY.LATITUDE }) || 0,
    lng: useWatch({ control, name: FORM_REGISTER_KEY.LONGITUDE }) || 0,
  };

  const [address, setAddress] = useState(EMPTY_STRING);

  const handleSelect = async (value) => {
    try {
      const results = await geocodeByAddress(value);
      if (!results || !results[0]) return;

      const result = results[0];
      const { lat, lng } = result.geometry?.location || {};

      // Set latitude and longitude
      if (lat && lng) {
        setValue(FORM_REGISTER_KEY.LATITUDE, lat());
        setValue(FORM_REGISTER_KEY.LONGITUDE, lng());
      }
      setAddress(value);
      setAddressComponents(result);
    } catch (error) {
      console.error("Error in handleSelect:", error);
    }
  };

  const setCoordinatesValue = async (value) => {
    const lat = Math.trunc(value.latLng.lat() * 100000) / 100000;
    const lng = Math.trunc(value.latLng.lng() * 100000) / 100000;

    setValue(FORM_REGISTER_KEY.LATITUDE, lat);
    setValue(FORM_REGISTER_KEY.LONGITUDE, lng);

    if (!lat || !lng) return;

    try {
      const response = await fromLatLng(lat, lng);
      if (!response?.results?.length) return;

      const result = response.results[0];
      setAddressComponents(result);
    } catch (error) {
      console.error("Error fetching location data:", error);
    }
  };

  const setAddressComponents = (result) => {
    const addressComponents = result.address_components || [];
    const componentMap = {
      country: EMPTY_STRING, // country
      administrative_area_level_2: EMPTY_STRING, // city
      administrative_area_level_1: EMPTY_STRING, // state
    };

    for (const component of addressComponents) {
      const componentType = component.types[0];
      if (componentMap.hasOwnProperty(componentType)) {
        componentMap[componentType] = component.long_name;
      }
    }

    const findCountryId = allCountries.find(
      (f) => f.name === componentMap.country
    )?.id;

    // Update form values
    setValue(FORM_REGISTER_KEY.COUNTRY, findCountryId);
    setValue(FORM_REGISTER_KEY.ADDRESS_LINE_1, result.formatted_address);
    setValue(FORM_REGISTER_KEY.STATE, componentMap.administrative_area_level_1);
    setValue(FORM_REGISTER_KEY.CITY, componentMap.administrative_area_level_2);

    if (findCountryId) {
      clearErrors(FORM_REGISTER_KEY.COUNTRY);
    }
    if (result.formatted_address !== EMPTY_STRING) {
      clearErrors(FORM_REGISTER_KEY.ADDRESS_LINE_1);
    }
    if (componentMap.administrative_area_level_2 !== EMPTY_STRING) {
      clearErrors(FORM_REGISTER_KEY.CITY);
    }
    if (componentMap.administrative_area_level_1 !== EMPTY_STRING) {
      clearErrors(FORM_REGISTER_KEY.STATE);
    }
  };

  return (
    <div className="create-form-card">
      <div className="form-card-heading">
        <img src={Location} alt={EMPTY_STRING} />
        Enter Location
      </div>
      <PlacesAutocomplete
        value={address}
        onChange={setAddress}
        onSelect={handleSelect}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div key={1}>
            <input
              {...getInputProps({
                placeholder: "Search Places...",
                className: "location-search-input create-list-input",
              })}
            />
            <div className="autocomplete-dropdown-container">
              {loading && <div>Searching...</div>}
              {suggestions.map((suggestion, index) => {
                const className = suggestion.active
                  ? "suggestion-item--active address-search"
                  : "suggestion-item address-search";
                const style = suggestion.active
                  ? {
                      backgroundColor: "#f9f9f9",
                      cursor: "pointer",
                    }
                  : {
                      backgroundColor: "#ffffff",
                      cursor: "pointer",
                    };
                return (
                  <div
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style,
                    })}
                    key={index}
                  >
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
      <input
        type="text"
        className={`create-list-input ${
          errors.addressLine1 ? "border border-danger" : EMPTY_STRING
        }`}
        placeholder="Address Line 1"
        {...register("addressLine1")}
      />
      <input
        type="text"
        className={`create-list-input ${
          errors.addressLine2 ? "border border-danger" : EMPTY_STRING
        }`}
        placeholder="Address Line 2"
        {...register("addressLine2")}
      />
      <div className="row mb-4">
        <div className="col-12 col-lg-4">
          <CustomSelect
            placeholder="Select Country"
            options={getSelectOptions(allCountries, "name", "id")}
            className=""
            name="countryId"
            showError={false}
            isClearable={false}
            defaultStyle={false}
          />
        </div>
        <div className="col-12 col-lg-4">
          <input
            type="text"
            className={`create-list-input input-height ${
              errors.state ? "border border-danger" : EMPTY_STRING
            }`}
            placeholder="State"
            {...register("state")}
          />
        </div>
        <div className="col-12 col-lg-4">
          <input
            type="text"
            className={`create-list-input input-height ${
              errors.city ? "border border-danger" : EMPTY_STRING
            }`}
            placeholder="City"
            {...register("city")}
          />
        </div>
      </div>
      <GoogleMap
        zoom={13}
        id="googleMap"
        options={options}
        mapContainerStyle={containerStyle}
        center={coordinates}
        onClick={(value) => setCoordinatesValue(value)}
      >
        <Marker position={coordinates} />
      </GoogleMap>
    </div>
  );
};

const mapStateToProps = (state) => ({
  countryReducer: state.countryReducer,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(AddressAndLocation);
